// useAmbientAudio — fully synthesized ambient audio via the Web Audio
// API. NO external audio files: every texture is built from
// OscillatorNode + GainNode + BiquadFilterNode (+ a filtered noise
// buffer). Each of the 7 scenes has its own ambient "profile"; moving
// between scenes cross-fades the parameters over ~2s using
// setTargetAtTime so the transition is seamless.
//
// The engine exposes an imperative controller — play(), pause(),
// setScene(idx), destroy() — so the React <AmbientAudioController />
// can drive it from scene changes and a toggle button.
//
// Rules honoured:
//   - AudioContext is created lazily, only on the first play() (which is
//     itself only ever called from a user gesture).
//   - prefers-reduced-motion => the master gain is pinned at 0 (muted).
//   - Master gain target is 0.05 (very quiet, ambient).

import { useRef } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AmbientAudioController {
  /** Resume/create the context and fade the master gain up. */
  play: () => void;
  /** Fade the master gain down to silence (keeps the graph alive). */
  pause: () => void;
  /** Cross-fade to a scene's ambient profile (idx 0..6). */
  setScene: (sceneIndex: number) => void;
  /** Tear everything down and close the AudioContext. */
  destroy: () => void;
  /** True while the master gain is (targeting) audible. */
  isPlaying: () => boolean;
}

/** One scene's ambient texture, expressed as target parameters. */
interface AmbientProfile {
  /** Fundamental of the pad, in Hz. */
  rootFreq: number;
  /** Gain of the perfect-fifth voice (0..1). */
  fifthGain: number;
  /** Gain of the octave voice (0..1). */
  octaveGain: number;
  /** Lowpass cutoff for the pad, in Hz. */
  filterFreq: number;
  /** Pad filter resonance. */
  filterQ: number;
  /** Filter LFO rate, in Hz (slow sweeps). */
  lfoRate: number;
  /** Filter LFO depth, in Hz. */
  lfoDepth: number;
  /** Vibrato depth on the root voice, in cents. */
  vibratoDepth: number;
  /** Filtered-noise layer gain (0..1). */
  noiseGain: number;
  /** Noise filter centre/cutoff, in Hz. */
  noiseFilterFreq: number;
  /** Noise filter type. */
  noiseFilterType: BiquadFilterType;
  /** Arpeggio voice gain (0..1) — only Timeline uses it. */
  arpGain: number;
  /** Overall pad bus gain (0..1). */
  padGain: number;
}

// ---------------------------------------------------------------------------
// Per-scene profiles. Index matches SCENES[] order:
// 0 Wake · 1 About · 2 Skills · 3 Projects · 4 Notes · 5 Timeline · 6 Contact
// ---------------------------------------------------------------------------

const PROFILES: AmbientProfile[] = [
  // 0 — Wake: warm low pad, slow filter LFO, very calm.
  {
    rootFreq: 110,
    fifthGain: 0.0,
    octaveGain: 0.14,
    filterFreq: 380,
    filterQ: 1.2,
    lfoRate: 0.08,
    lfoDepth: 140,
    vibratoDepth: 0,
    noiseGain: 0.0,
    noiseFilterFreq: 800,
    noiseFilterType: 'lowpass',
    arpGain: 0.0,
    padGain: 0.9,
  },
  // 1 — About: mid pad with subtle vibrato + slight melodic drift.
  {
    rootFreq: 220,
    fifthGain: 0.12,
    octaveGain: 0.1,
    filterFreq: 900,
    filterQ: 1.4,
    lfoRate: 0.16,
    lfoDepth: 220,
    vibratoDepth: 8,
    noiseGain: 0.0,
    noiseFilterFreq: 1200,
    noiseFilterType: 'lowpass',
    arpGain: 0.0,
    padGain: 0.85,
  },
  // 2 — Skills: high pad + shimmer (high-passed noise, wide filter sweep).
  {
    rootFreq: 330,
    fifthGain: 0.1,
    octaveGain: 0.2,
    filterFreq: 1600,
    filterQ: 2.0,
    lfoRate: 0.4,
    lfoDepth: 900,
    vibratoDepth: 4,
    noiseGain: 0.22,
    noiseFilterFreq: 4200,
    noiseFilterType: 'highpass',
    arpGain: 0.0,
    padGain: 0.7,
  },
  // 3 — Projects: darker pad, lower fundamental, closed filter.
  {
    rootFreq: 82,
    fifthGain: 0.08,
    octaveGain: 0.05,
    filterFreq: 260,
    filterQ: 1.0,
    lfoRate: 0.05,
    lfoDepth: 70,
    vibratoDepth: 0,
    noiseGain: 0.0,
    noiseFilterFreq: 600,
    noiseFilterType: 'lowpass',
    arpGain: 0.0,
    padGain: 0.95,
  },
  // 4 — Notes: airy pad + gently band-passed white noise.
  {
    rootFreq: 196,
    fifthGain: 0.1,
    octaveGain: 0.12,
    filterFreq: 1100,
    filterQ: 1.5,
    lfoRate: 0.2,
    lfoDepth: 320,
    vibratoDepth: 3,
    noiseGain: 0.16,
    noiseFilterFreq: 1500,
    noiseFilterType: 'bandpass',
    arpGain: 0.0,
    padGain: 0.75,
  },
  // 5 — Timeline: rising arpeggio-like voice (C E G B) over a low pad.
  {
    rootFreq: 130.81, // C3
    fifthGain: 0.06,
    octaveGain: 0.08,
    filterFreq: 1200,
    filterQ: 1.6,
    lfoRate: 0.25,
    lfoDepth: 300,
    vibratoDepth: 2,
    noiseGain: 0.0,
    noiseFilterFreq: 1000,
    noiseFilterType: 'lowpass',
    arpGain: 0.5,
    padGain: 0.5,
  },
  // 6 — Contact: full, warm chord pad (root + fifth + octave).
  {
    rootFreq: 130.81, // C3
    fifthGain: 0.2,
    octaveGain: 0.18,
    filterFreq: 1000,
    filterQ: 1.3,
    lfoRate: 0.1,
    lfoDepth: 180,
    vibratoDepth: 5,
    noiseGain: 0.0,
    noiseFilterFreq: 1000,
    noiseFilterType: 'lowpass',
    arpGain: 0.0,
    padGain: 0.9,
  },
];

/** Arpeggio note cycle for the Timeline scene: C4 E4 G4 B4. */
const ARP_NOTES = [261.63, 329.63, 392.0, 493.88];

const MASTER_LEVEL = 0.05;
const CROSSFADE_TC = 0.7; // setTargetAtTime time-constant → ~2s to settle.
const RAMP_TC = 0.5; // play/pause master fade.

// ---------------------------------------------------------------------------
// Engine
// ---------------------------------------------------------------------------

type AudioCtor = typeof AudioContext;

function getAudioCtor(): AudioCtor | null {
  if (typeof window === 'undefined') return null;
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: AudioCtor }).webkitAudioContext ??
    null
  );
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function createAmbientAudioEngine(): AmbientAudioController {
  let ctx: AudioContext | null = null;
  let master: GainNode | null = null;

  // Pad voices.
  let oscRoot: OscillatorNode | null = null;
  let oscFifth: OscillatorNode | null = null;
  let oscOctave: OscillatorNode | null = null;
  let gainRoot: GainNode | null = null;
  let gainFifth: GainNode | null = null;
  let gainOctave: GainNode | null = null;
  let padFilter: BiquadFilterNode | null = null;
  let padBus: GainNode | null = null;

  // Modulation.
  let filterLfo: OscillatorNode | null = null;
  let filterLfoGain: GainNode | null = null;
  let vibratoLfo: OscillatorNode | null = null;
  let vibratoGain: GainNode | null = null;

  // Noise layer.
  let noiseSrc: AudioBufferSourceNode | null = null;
  let noiseFilter: BiquadFilterNode | null = null;
  let noiseGainNode: GainNode | null = null;

  // Arpeggio voice.
  let arpOsc: OscillatorNode | null = null;
  let arpGainNode: GainNode | null = null;
  let arpTimer: number | null = null;
  let arpStep = 0;

  let started = false;
  let playing = false;
  let currentScene = 0;

  // -------------------------------------------------------------------------
  function buildNoiseBuffer(context: AudioContext): AudioBuffer {
    const seconds = 2;
    const buffer = context.createBuffer(
      1,
      context.sampleRate * seconds,
      context.sampleRate,
    );
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    return buffer;
  }

  function ensureGraph() {
    if (started) return;
    const Ctor = getAudioCtor();
    if (!Ctor) return;
    ctx = new Ctor();

    master = ctx.createGain();
    master.gain.value = 0.0001; // start silent; play() fades up.
    master.connect(ctx.destination);

    // ---- Pad bus ----
    padBus = ctx.createGain();
    padBus.gain.value = 0.9;
    padFilter = ctx.createBiquadFilter();
    padFilter.type = 'lowpass';
    padFilter.frequency.value = 400;
    padFilter.Q.value = 1;
    padBus.connect(padFilter);
    padFilter.connect(master);

    // Root voice.
    oscRoot = ctx.createOscillator();
    oscRoot.type = 'sawtooth';
    oscRoot.frequency.value = 110;
    gainRoot = ctx.createGain();
    gainRoot.gain.value = 0.5;
    oscRoot.connect(gainRoot);
    gainRoot.connect(padBus);

    // Fifth voice (7 semitones above root ≈ ×1.5).
    oscFifth = ctx.createOscillator();
    oscFifth.type = 'triangle';
    oscFifth.frequency.value = 110 * 1.5;
    gainFifth = ctx.createGain();
    gainFifth.gain.value = 0;
    oscFifth.connect(gainFifth);
    gainFifth.connect(padBus);

    // Octave voice.
    oscOctave = ctx.createOscillator();
    oscOctave.type = 'sine';
    oscOctave.frequency.value = 220;
    gainOctave = ctx.createGain();
    gainOctave.gain.value = 0;
    oscOctave.connect(gainOctave);
    gainOctave.connect(padBus);

    // ---- Filter LFO (slow sweep of padFilter cutoff) ----
    filterLfo = ctx.createOscillator();
    filterLfo.type = 'sine';
    filterLfo.frequency.value = 0.08;
    filterLfoGain = ctx.createGain();
    filterLfoGain.gain.value = 120;
    filterLfo.connect(filterLfoGain);
    filterLfoGain.connect(padFilter.frequency);

    // ---- Vibrato LFO (detune root voice) ----
    vibratoLfo = ctx.createOscillator();
    vibratoLfo.type = 'sine';
    vibratoLfo.frequency.value = 5;
    vibratoGain = ctx.createGain();
    vibratoGain.gain.value = 0;
    vibratoLfo.connect(vibratoGain);
    vibratoGain.connect(oscRoot.detune);

    // ---- Noise layer ----
    noiseSrc = ctx.createBufferSource();
    noiseSrc.buffer = buildNoiseBuffer(ctx);
    noiseSrc.loop = true;
    noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = 'highpass';
    noiseFilter.frequency.value = 2000;
    noiseFilter.Q.value = 0.8;
    noiseGainNode = ctx.createGain();
    noiseGainNode.gain.value = 0;
    noiseSrc.connect(noiseFilter);
    noiseFilter.connect(noiseGainNode);
    noiseGainNode.connect(master);

    // ---- Arpeggio voice ----
    arpOsc = ctx.createOscillator();
    arpOsc.type = 'triangle';
    arpOsc.frequency.value = ARP_NOTES[0];
    arpGainNode = ctx.createGain();
    arpGainNode.gain.value = 0;
    arpOsc.connect(arpGainNode);
    arpGainNode.connect(master);

    // Start all always-on sources.
    const t0 = ctx.currentTime;
    oscRoot.start(t0);
    oscFifth.start(t0);
    oscOctave.start(t0);
    filterLfo.start(t0);
    vibratoLfo.start(t0);
    noiseSrc.start(t0);
    arpOsc.start(t0);

    started = true;
    // Apply the initial scene profile immediately (no audible fade yet
    // since master is ~silent).
    applyProfile(currentScene, 0.01);
  }

  function applyProfile(idx: number, tc: number) {
    if (!ctx) return;
    const p = PROFILES[idx] ?? PROFILES[0];
    const now = ctx.currentTime;

    const set = (param: AudioParam | undefined, value: number) => {
      if (param) param.setTargetAtTime(value, now, tc);
    };

    set(oscRoot?.frequency, p.rootFreq);
    set(oscFifth?.frequency, p.rootFreq * 1.5);
    set(oscOctave?.frequency, p.rootFreq * 2);
    set(gainFifth?.gain, p.fifthGain);
    set(gainOctave?.gain, p.octaveGain);
    set(padFilter?.frequency, p.filterFreq);
    set(padFilter?.Q, p.filterQ);
    set(filterLfo?.frequency, p.lfoRate);
    set(filterLfoGain?.gain, p.lfoDepth);
    set(vibratoGain?.gain, p.vibratoDepth);
    set(noiseGainNode?.gain, p.noiseGain);
    set(noiseFilter?.frequency, p.noiseFilterFreq);
    set(arpGainNode?.gain, p.arpGain);
    set(padBus?.gain, p.padGain);
    if (noiseFilter) noiseFilter.type = p.noiseFilterType;

    // Arpeggio scheduler runs only for the Timeline profile.
    if (p.arpGain > 0) startArp();
    else stopArp();
  }

  function startArp() {
    if (arpTimer !== null || typeof window === 'undefined') return;
    arpStep = 0;
    arpTimer = window.setInterval(() => {
      if (!ctx || !arpOsc) return;
      const note = ARP_NOTES[arpStep % ARP_NOTES.length];
      arpOsc.frequency.setTargetAtTime(note, ctx.currentTime, 0.05);
      arpStep++;
    }, 420);
  }

  function stopArp() {
    if (arpTimer !== null) {
      window.clearInterval(arpTimer);
      arpTimer = null;
    }
  }

  // -------------------------------------------------------------------------
  return {
    play() {
      if (prefersReducedMotion()) {
        // Muted completely for reduced-motion users.
        playing = false;
        return;
      }
      ensureGraph();
      if (!ctx || !master) return;
      if (ctx.state === 'suspended') void ctx.resume();
      master.gain.setTargetAtTime(MASTER_LEVEL, ctx.currentTime, RAMP_TC);
      playing = true;
    },

    pause() {
      if (!ctx || !master) {
        playing = false;
        return;
      }
      master.gain.setTargetAtTime(0.0001, ctx.currentTime, RAMP_TC);
      playing = false;
    },

    setScene(sceneIndex: number) {
      currentScene = Math.max(0, Math.min(PROFILES.length - 1, sceneIndex | 0));
      if (!started) return; // profile applied on ensureGraph().
      applyProfile(currentScene, CROSSFADE_TC);
    },

    destroy() {
      stopArp();
      try {
        const stop = (n: { stop?: (t?: number) => void } | null) => {
          try {
            n?.stop?.();
          } catch {
            /* already stopped */
          }
        };
        stop(oscRoot);
        stop(oscFifth);
        stop(oscOctave);
        stop(filterLfo);
        stop(vibratoLfo);
        stop(noiseSrc);
        stop(arpOsc);
        void ctx?.close();
      } catch {
        /* ignore teardown errors */
      }
      ctx = null;
      master = null;
      oscRoot = oscFifth = oscOctave = null;
      gainRoot = gainFifth = gainOctave = null;
      padFilter = null;
      padBus = null;
      filterLfo = vibratoLfo = null;
      filterLfoGain = vibratoGain = null;
      noiseSrc = null;
      noiseFilter = null;
      noiseGainNode = null;
      arpOsc = null;
      arpGainNode = null;
      started = false;
      playing = false;
    },

    isPlaying() {
      return playing;
    },
  };
}

// ---------------------------------------------------------------------------
// Hook — memoises a single engine instance for the component lifetime and
// tears it down on unmount.
// ---------------------------------------------------------------------------

export function useAmbientAudio(): AmbientAudioController {
  const ref = useRef<AmbientAudioController | null>(null);
  if (ref.current === null) {
    ref.current = createAmbientAudioEngine();
  }
  return ref.current;
}
